import React, { useEffect, useState, useCallback, useRef } from 'react';
import './OpenSpaceOnboarding.css';

// Onboarding "film" for Öppna ytor: real screenshots of the tool (taken with
// frontend/scripts/onboarding-shots/shoot.js against the dev server + a mocked
// API) shown as a stepped sequence with an animated cursor, click rings and
// crossfades between frames. Autoplays with a progress bar, can be stepped
// manually. Shown once on first visit (localStorage key below) and on demand
// via the "Så fungerar det" button.
//
// All overlay positions are FRACTIONS of the 16:10 frame (0..1), measured on
// the 1140 x 713 px crop the shoot script produces (controls row + map).

export const ONBOARDING_SEEN_KEY = 'os-onboarding-seen-v1';

export const hasSeenOnboarding = () => {
  try { return localStorage.getItem(ONBOARDING_SEEN_KEY) === '1'; } catch { return true; }
};

export const markOnboardingSeen = () => {
  try { localStorage.setItem(ONBOARDING_SEEN_KEY, '1'); } catch { /* ignore */ }
};

const IMG_BASE = `${process.env.PUBLIC_URL || ''}/onboarding/open-space`;
const img = (name) => `${IMG_BASE}/${name}.jpg`;

// Button centres in the controls row (result state has the same row).
const BTN = {
  select: { x: 0.052, y: 0.04 },
  analyze: { x: 0.16, y: 0.04 },
  png: { x: 0.28, y: 0.04 },
  streetview: { x: 0.394, y: 0.04 },
};

// A scene = frames (which image is visible from time `at` ms), a cursor path
// (position reached at `at`; the cursor glides between points), clicks
// (ring at `at`) and optional `cards` (overlays with their own timing).
const STEPS = [
  {
    title: 'Hitta tomma ytor i staden',
    text: 'Verktyget letar efter sammanhängande mark där en kvadrat på 45 × 45 m får plats utan byggnader eller vatten. Gator, vägar och trädtäckning räknas som öppen mark. Resultatet ritas direkt på satellitbilden.',
    duration: 7000,
    // Current UI (yellow borders, single layer checkbox): 00-intro.jpg is a
    // real screenshot cropped to the same 16:10 frame as the shoot-script
    // frames. Scenes 2-5 still use the older Vasastan frames so the
    // click-through stays in one place.
    frames: [{ src: img('00-intro'), at: 0 }],
    cursor: [],
    clicks: [],
    zoom: true,
  },
  {
    title: '1. Välj område',
    text: 'Klicka på "Välj område" och sedan två gånger på kartan – ett hörn och det motsatta. Rektangeln kan dras och ändras i storlek efteråt. Max 4 km².',
    duration: 8500,
    frames: [
      { src: img('02-empty'), at: 0 },
      { src: img('02-rubber'), at: 3300 },
      { src: img('02-locked'), at: 5600 },
    ],
    cursor: [
      { x: 0.35, y: 0.6, at: 0 },
      { x: BTN.select.x, y: BTN.select.y, at: 900 },
      { x: 0.30, y: 0.299, at: 2400 },
      { x: 0.50, y: 0.57, at: 3600 },
      { x: 0.70, y: 0.871, at: 4900 },
    ],
    clicks: [
      { x: BTN.select.x, y: BTN.select.y, at: 1000 },
      { x: 0.30, y: 0.299, at: 2500 },
      { x: 0.70, y: 0.871, at: 5500 },
    ],
  },
  {
    title: '2. Analysera',
    text: 'Tryck "Analysera". Alla ytor där kvadraten får plats ritas ut med gul kant. Tar normalt 10–60 sekunder.',
    duration: 8000,
    frames: [
      { src: img('02-locked'), at: 0 },
      { src: img('03-analyzing'), at: 1900 },
      { src: img('01-result'), at: 5200 },
    ],
    cursor: [
      { x: 0.55, y: 0.6, at: 0 },
      { x: BTN.analyze.x, y: BTN.analyze.y, at: 1500 },
      { x: BTN.analyze.x, y: BTN.analyze.y, at: 2300 },
      { x: 0.58, y: 0.25, at: 3500 },
    ],
    clicks: [{ x: BTN.analyze.x, y: BTN.analyze.y, at: 1700 }],
  },
  {
    title: '3. Läs resultatet',
    text: 'Tabellen listar varje yta med typ, area och största kvadrat som får plats. Klicka på en rad för att zooma in på ytan. Kryssrutan ovanför kartan tänder och släcker numren.',
    duration: 8000,
    frames: [
      { src: img('04-table'), at: 0 },
      { src: img('04-zoom'), at: 3600 },
    ],
    cursor: [
      { x: 0.5, y: 0.8, at: 0 },
      { x: 0.12, y: 0.198, at: 2200 },
      { x: 0.12, y: 0.198, at: 3400 },
      { x: 0.45, y: 0.75, at: 4800 },
    ],
    clicks: [{ x: 0.12, y: 0.198, at: 3300 }],
  },
  {
    title: '4. Titta i gatunivå',
    text: 'Med "Gatuvy" klickar du först på en gata där du vill stå och sedan i den riktning du vill titta. Närmaste Street View-panorama öppnas vänd åt det hållet.',
    duration: 10000,
    frames: [
      { src: img('01-result'), at: 0 },
      { src: img('05-point'), at: 4000 },
      { src: img('05-pano'), at: 7200 },
    ],
    cursor: [
      { x: 0.6, y: 0.5, at: 0 },
      { x: BTN.streetview.x, y: BTN.streetview.y, at: 1200 },
      { x: BTN.streetview.x, y: BTN.streetview.y, at: 1800 },
      { x: 0.351, y: 0.678, at: 3400 },
      { x: 0.351, y: 0.678, at: 4300 },
      { x: 0.526, y: 0.654, at: 5800 },
      { x: 0.526, y: 0.654, at: 7000 },
      { x: 0.7, y: 0.75, at: 8200 },
    ],
    clicks: [
      { x: BTN.streetview.x, y: BTN.streetview.y, at: 1300 },
      { x: 0.351, y: 0.678, at: 3900 },
      { x: 0.526, y: 0.654, at: 6800 },
    ],
  },
  {
    title: '5. Spara och återbesök',
    text: '"Spara som PNG" laddar ner en satellitbild med ytorna, numren och måtten inritade. Dina tidigare analyser listas under kartan och kan öppnas igen när som helst.',
    duration: 8000,
    frames: [{ src: img('00-intro'), at: 0 }],
    cursor: [
      { x: 0.5, y: 0.6, at: 0 },
      { x: BTN.png.x, y: BTN.png.y, at: 1400 },
      { x: BTN.png.x, y: BTN.png.y, at: 2400 },
      { x: 0.2, y: 0.9, at: 4200 },
    ],
    clicks: [{ x: BTN.png.x, y: BTN.png.y, at: 1600 }],
    cards: [
      { kind: 'download', at: 1900, until: 4400 },
      { kind: 'image', src: img('06-previous'), at: 4400 },
    ],
  },
];

// ---- scene player -------------------------------------------------------

const Scene = ({ step }) => {
  const [t, setT] = useState(0);
  const start = useRef(performance.now());

  // Coarse clock (one state update per animation frame); the visual
  // smoothness comes from CSS transitions on the cursor and frames.
  useEffect(() => {
    start.current = performance.now();
    setT(0);
    let raf;
    const tick = () => {
      setT(performance.now() - start.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [step]);

  const frameIdx = step.frames.reduce((acc, f, i) => (t >= f.at ? i : acc), 0);
  const cur = step.cursor.reduce((acc, c) => (t >= c.at ? c : acc), null);
  const cursorVisible = step.cursor.length > 0 && t > 200;

  return (
    <div className={`osb-scene ${step.zoom ? 'osb-scene-zoom' : ''}`}>
      {step.frames.map((f, i) => (
        <img
          key={f.src + i}
          src={f.src}
          alt=""
          className={`osb-frame ${i === frameIdx ? 'osb-frame-on' : ''}`}
          draggable={false}
        />
      ))}
      {(step.cards || []).map((c, i) => {
        const on = t >= c.at && (c.until == null || t < c.until);
        if (c.kind === 'download') {
          return (
            <div key={i} className={`osb-card osb-card-download ${on ? 'osb-card-on' : ''}`}>
              <span className="osb-dl-icon">⬇</span> Midsommarkransen 2026-09-08.png
            </div>
          );
        }
        return (
          <div key={i} className={`osb-card osb-card-image ${on ? 'osb-card-on' : ''}`}>
            <img src={c.src} alt="" draggable={false} />
          </div>
        );
      })}
      {step.clicks.map((c, i) => (
        <span
          key={i}
          className={`osb-ring ${t >= c.at ? 'osb-ring-on' : ''}`}
          style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%` }}
        />
      ))}
      {cur && (
        <svg
          className={`osb-cursor ${cursorVisible ? 'osb-cursor-on' : ''}`}
          style={{ left: `${cur.x * 100}%`, top: `${cur.y * 100}%` }}
          viewBox="0 0 14 20"
          width="22"
          height="31"
        >
          <path d="M1 1 L1 16 L5 12 L8 19 L11 18 L8 11 L13 11 Z" fill="#111" stroke="#fff" strokeWidth="1.4" />
        </svg>
      )}
    </div>
  );
};

// ---- component -----------------------------------------------------------

const OpenSpaceOnboarding = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [tick, setTick] = useState(0);   // bumps to restart the active scene

  const last = STEPS.length - 1;

  const goTo = useCallback((i, keepPlaying = false) => {
    setStep(Math.max(0, Math.min(last, i)));
    setTick(x => x + 1);
    if (!keepPlaying) setPlaying(false);
  }, [last]);

  // Reset + preload all frames when (re)opened
  useEffect(() => {
    if (!open) return;
    setStep(0); setPlaying(true); setTick(x => x + 1);
    STEPS.forEach(s => {
      s.frames.forEach(f => { const im = new Image(); im.src = f.src; });
      (s.cards || []).forEach(c => { if (c.src) { const im = new Image(); im.src = c.src; } });
    });
  }, [open]);

  // Autoplay: advance after the scene's duration, stop at the end
  useEffect(() => {
    if (!open || !playing) return undefined;
    if (step >= last) { setPlaying(false); return undefined; }
    const id = setTimeout(() => goTo(step + 1, true), STEPS[step].duration);
    return () => clearTimeout(id);
  }, [open, playing, step, last, goTo, tick]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') goTo(step + 1);
      else if (e.key === 'ArrowLeft') goTo(step - 1);
      else if (e.key === ' ') { e.preventDefault(); setPlaying(p => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, step, goTo, onClose]);

  if (!open) return null;

  const current = STEPS[step];

  return (
    <div className="osb-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Så fungerar Tomma ytor">
      <div className="osb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="osb-head">
          <span className="osb-eyebrow">Så fungerar Tomma ytor</span>
          <span className="osb-counter">{step + 1} / {STEPS.length}</span>
          <button className="osb-close" onClick={onClose} aria-label="Stäng" data-tip="osb.close">×</button>
        </div>

        <div className="osb-stage">
          <Scene key={`${step}-${tick}`} step={current} />
          {playing && step < last && (
            <div className="osb-bar">
              <div className="osb-bar-fill" key={`bar-${step}-${tick}`} style={{ animationDuration: `${current.duration}ms` }} />
            </div>
          )}
        </div>

        <div className="osb-body">
          <h2>{current.title}</h2>
          <p>{current.text}</p>
        </div>

        <div className="osb-foot">
          <div className="osb-dots" role="tablist">
            {STEPS.map((s, i) => (
              <button
                key={i}
                className={`osb-dot ${i === step ? 'osb-dot-active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={s.title}
                data-tip={s.title}
              />
            ))}
          </div>
          <div className="osb-nav">
            <button onClick={() => goTo(step - 1)} disabled={step === 0} data-tip="osb.prev">‹ Föregående</button>
            <button onClick={() => setPlaying(p => !p)} disabled={step === last} data-tip="osb.play">
              {playing ? 'Pausa' : 'Spela'}
            </button>
            {step < last ? (
              <button className="osb-primary" onClick={() => goTo(step + 1)} data-tip="osb.next">Nästa ›</button>
            ) : (
              <button className="osb-primary" onClick={onClose} data-tip="osb.done">Börja använda</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenSpaceOnboarding;
